import * as vp from 'vopay';
import React, { FunctionComponent } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import { UpdateWebhooksHandler } from './types';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Title, Table, TextField, Toggle, BackButton } = vp;

type DefaultValues = {
  accountonboarding: string;
};

type ToggleValues = {
  accountonboarding: boolean;
};

type UpdateWebhookProps = {
  validatedWebhookEvents: vp.WebhookEvent[];
  defaultValues: DefaultValues;
  toggleValues: ToggleValues;
  setToggleValues: React.Dispatch<React.SetStateAction<ToggleValues>>;
  onSubmit: (values: DefaultValues) => void;
  setIsVisible: (e: boolean) => void;
  handlers: UpdateWebhooksHandler;
};
type FC = FunctionComponent<UpdateWebhookProps>;

const UpdateWebhook: FC = (props: UpdateWebhookProps) => {
  const { validatedWebhookEvents, defaultValues, toggleValues, setToggleValues, onSubmit, setIsVisible, handlers } =
    props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const updateWebhookRows = useBuildUpdateWebhookRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks.Update' });

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      onSubmit={(values) => onSubmit(values)}
      validateOnChange
      validateOnBlur
      validationSchema={createdValidationSchema()}
    >
      {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
        // A toggled-on field blocks Review if it's empty or fails URL validation; toggled-off fields are ignored.
        const isDisabled = updateWebhookRows.some(
          ({ id }) =>
            toggleValues[id as keyof ToggleValues] &&
            (!values[id as keyof DefaultValues] || !!errors[id as keyof DefaultValues]),
        );
        // An event can be retriggered only if it has a saved URL and the user hasn't edited it.
        const retriggerableEvents = validatedWebhookEvents.filter(
          (event) => values[event as keyof DefaultValues] === defaultValues[event as keyof DefaultValues],
        );

        const updateWebhookTable: vp.TableProps = {
          name: 'update-webhook-fields',
          columns: {
            Field: { header: t('Form.event', { defaultValue: 'Event' }) },
            Toggle: { header: t('Form.enable', { defaultValue: 'Enable Events' }), align: 'left' },
            Input: { header: t('Form.url', { defaultValue: 'URL' }) },
            Retrigger: { header: '' },
          },
          rows: updateWebhookRows.map(({ label, id, preset }) => ({
            Field: label,
            Toggle: (
              <Box flx="row" jc="fs" ps="sm">
                <Toggle
                  name={id}
                  onBlur={handleBlur}
                  onChange={() => {
                    const isEnabling = !toggleValues[id as keyof ToggleValues];
                    setToggleValues((prev) => ({ ...prev, [id]: isEnabling }));
                    if (!isEnabling) setFieldValue(id, '');
                  }}
                  value={toggleValues[id as keyof ToggleValues]}
                />
              </Box>
            ),
            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                placeholder={t('Form.urlPlaceholder', { defaultValue: 'Provide URL' })}
                onBlur={handleBlur}
                disabled={!toggleValues[id as keyof ToggleValues]}
                onChange={handleChange}
                value={values[id as keyof DefaultValues]}
                error={errors[id as keyof DefaultValues] ? errors[id as keyof DefaultValues] : null}
              />
            ),
            Retrigger:
              retriggerableEvents.includes(id) && !!defaultValues.accountonboarding ? (
                <Button
                  id="Webhook-review"
                  type="button"
                  color="secondary"
                  label={t('Form.retriggerButton', { defaultValue: 'Retrigger' })}
                  onClick={() => {
                    handlers.setWebhookType({ label, id });
                    setIsVisible(true);
                  }}
                />
              ) : (
                ''
              ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Box mb="md" jc="fs" ai="center" gap="common">
              <BackButton variant="arrow" backButtonEnabled />
              <Title title={t('pageTitle', { defaultValue: 'Manage Webhook' })} />
            </Box>
            <Table {...updateWebhookTable} />
            <Box mt="md" jc="fe">
              <Button
                id="Webhook-review"
                type="submit"
                label={t('Form.reviewButton', { defaultValue: 'Review' })}
                isDisabled={isDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      accountonboarding: Yup.string().url(t('Form.invalidURLValidation', { defaultValue: 'Please enter a valid URL.' })),
    });
  }
};

export default UpdateWebhook;
